import React, { useCallback, useRef, useEffect, useState } from 'react';
import ReactFlow, {
    addEdge,
    Background,
    useEdgesState,
    useNodesState,
    Connection,
    Edge,
    Handle,
    Position,
    useReactFlow,
    ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FaSave } from '@react-icons/all-files/fa/FaSave';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import { FaSyncAlt } from '@react-icons/all-files/fa/FaSyncAlt';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaExchangeAlt } from '@react-icons/all-files/fa/FaExchangeAlt';
import { FaSearchPlus } from '@react-icons/all-files/fa/FaSearchPlus';
import { FaSearchMinus } from '@react-icons/all-files/fa/FaSearchMinus';
import { FaExpand } from '@react-icons/all-files/fa/FaExpand';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import WorkflowDisenoTabPage from './workflow-diseno-tab-panel-page';
import ShieldIcon from '../../../../commons/ShieldIcon';

// ── Toolbar ───────────────────────────────────────────────────────────────

interface WorkflowToolbarProps {
    saveWorkflow: () => void;
    volver: () => void;
    onRefresh?: () => void;
    onReplace?: () => void;
    selectedNode: any;
    selectedEdge: any;
    removeNode: (node: any) => void;
    removeEdge: (edge: any) => void;
    onCancelSelection: () => void;
    addNodeFromSelected: (id: string) => void;
    allowDelete: boolean;
    allowAddNode: boolean;
}

function WorkflowToolbar({
    saveWorkflow,
    volver,
    onRefresh,
    onReplace,
    selectedNode,
    selectedEdge,
    removeNode,
    removeEdge,
    onCancelSelection,
    addNodeFromSelected,
    allowDelete,
    allowAddNode,

}: WorkflowToolbarProps) {
    const { zoomIn, zoomOut, fitView, setNodes, setEdges } = useReactFlow();
    const hasSelection = !!(selectedNode || selectedEdge);
    const allowDeleteButton = selectedEdge || (selectedNode && allowDelete);

    const handleDelete = () => {
        if (selectedNode) removeNode(selectedNode);
        else if (selectedEdge) removeEdge(selectedEdge);
    };

    const handleCancel = () => {
        // Clear ReactFlow's internal visual selection (node highlight / edge highlight)
        setNodes((nds: any[]) => nds.map(n => ({ ...n, selected: false })));
        setEdges((eds: any[]) => eds.map(e => ({ ...e, selected: false })));
        onCancelSelection();
    };

    return (
        <div style={tb.bar}>
            {/* Navigation */}
            <TBtn icon={<FaArrowLeft />} label="Volver" onClick={volver} />
            {onRefresh && <TBtn icon={<FaSyncAlt />} label="Refrescar" onClick={onRefresh} />}

            <div style={tb.sep} />

            {/* Actions */}
            <TBtn icon={<FaSave />} label="Guardar" onClick={saveWorkflow} accent compact />
            <TBtn icon={<FaPlus />} label="Agregar nodo hijo" onClick={() => addNodeFromSelected(selectedNode.id)}
                disabled={!selectedNode || !allowAddNode} compact />

            <TBtn icon={<FaTrash />} label="Eliminar" onClick={handleDelete}
                danger disabled={!hasSelection || !allowDeleteButton} compact
                title={hasSelection ? 'Eliminar' : 'Selecciona un nodo o transición'} />

            <TBtn icon={<FaTimes />} label="Cancelar" onClick={handleCancel}
                disabled={!hasSelection} compact />
            {onReplace && <TBtn icon={<FaExchangeAlt />} label="Reemplazar" onClick={onReplace} />}

            <div style={tb.sep} />

            {/* View */}
            <TBtn icon={<FaSearchPlus />} label="Acercar" onClick={() => zoomIn()} compact />
            <TBtn icon={<FaSearchMinus />} label="Alejar" onClick={() => zoomOut()} compact />
            <TBtn icon={<FaExpand />} label="Ajustar" onClick={() => fitView()} compact />
            {allowDelete ? 'delete' : 'no'}
        </div>
    );
}

interface TBtnProps {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    accent?: boolean;
    danger?: boolean;
    disabled?: boolean;
    compact?: boolean;
    title?: string;
}

function TBtn({ icon, label, onClick, accent, danger, disabled, compact, title }: TBtnProps) {
    const base: React.CSSProperties = {
        display: 'inline-flex', alignItems: 'center', gap: compact ? 0 : 5,
        padding: compact ? '5px 8px' : '5px 11px',
        borderRadius: 6, border: '1px solid',
        fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.12s, opacity 0.12s',
        opacity: disabled ? 0.42 : 1,
        whiteSpace: 'nowrap' as const,
        ...(accent
            ? { background: '#6366f1', color: '#fff', borderColor: '#6366f1' }
            : danger
                ? { background: '#fff5f5', color: '#dc2626', borderColor: '#fecaca' }
                : { background: '#fff', color: '#475569', borderColor: '#e2e8f0' }),
    };
    return (
        <button style={base} onClick={disabled ? undefined : onClick} title={title ?? label} type="button">
            {icon}
            {!compact && <span>{label}</span>}
        </button>
    );
}

const tb: Record<string, React.CSSProperties> = {
    bar: {
        display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
        padding: '6px 10px', background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0', flexShrink: 0,
    },
    sep: { width: 1, height: 22, background: '#e2e8f0', margin: '0 2px' },
};

// ── Nodos personalizados ───────────────────────────────────────────────────

function EndNodeFn({
    data,
    selected,
}: {
    data: any;
    selected: boolean;
}) {
    return <>{createHandle('target', Position.Top)}<div
        className={selected ? 'nodo selected nodo-final' : 'nodo nodo-final'}
        style={{
            border: selected ? '2px solid #2563eb' : '2px solid #777',
            color: selected ? 'black' : '',
            boxShadow: selected
                ? '0 0 0 2px rgba(37,99,235,0.2)'
                : 'none',
            transition: 'all 0.15s ease',
            textAlign: 'center'
        }}
    >
        <div className='nodo-final-borde'></div>
    </div>
    </>
}

const createHandle = (type: any, position: any) => {
    return <Handle
        type={type}
        position={position}
        style={{
            width: 12,
            height: 12,
            background: 'white',
            borderRadius: '50%',
            border: '2px solid black'
        }}
    />
}

// ── Node user avatar (drag-drop target) ───────────────────────────────────

const DRAG_TYPE = 'application/x-profase-usuario';

/* ---------- SHIELD COMPONENT (now in src/commons/ShieldIcon.tsx) ---------- */
function RequiereAutorizacionShield({ show }: { show: boolean }) {
    return <ShieldIcon show={show} />;
}

function NodeUserAvatar({ user, size = 28, onAssign }: { user?: any; size?: number; onAssign?: (u: any) => void }) {
    const [imgError, setImgError] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    const onDragOver = (e: React.DragEvent) => {
        if (!onAssign) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
    };
    const onDragLeave = () => setIsDragOver(false);
    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        if (!onAssign) return;
        try {
            const raw = e.dataTransfer.getData(DRAG_TYPE);
            if (raw) onAssign(JSON.parse(raw));
        } catch { }
    };

    const dropBorder = isDragOver ? '2px solid #22c55e' : undefined;
    const dropShadow = isDragOver ? '0 0 0 3px rgba(34,197,94,0.3)' : 'none';
    const dropBg = isDragOver ? '#f0fdf4' : undefined;
    const base: React.CSSProperties = {
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        cursor: onAssign ? 'copy' : 'default',
        transition: 'border 0.12s, box-shadow 0.12s, background 0.12s',
    };
    const dropProps = { onDragOver, onDragLeave, onDrop };

    if (!user?.id) {
        return (
            <div
                title="Soltar usuario aquí"
                {...dropProps}
                style={{
                    ...base,
                    background: dropBg ?? '#f1f5f9',
                    border: dropBorder ?? '1.5px dashed #cbd5e1',
                    boxShadow: dropShadow,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                <svg viewBox="0 0 24 24" width={size * 0.65} height={size * 0.65}
                    fill={isDragOver ? '#22c55e' : '#94a3b8'} xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                </svg>
            </div>
        );
    }

    const initials = `${(user.nombre ?? '')[0] ?? ''}${(user.apellido ?? '')[0] ?? ''}`.toUpperCase();
    const title = `${user.nombre ?? ''} ${user.apellido ?? ''}`.trim();

    if (imgError) {
        return (
            <div title={title} {...dropProps} style={{
                ...base,
                background: '#e0e7ff', color: '#6366f1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: Math.round(size * 0.38), fontWeight: 700,
                border: dropBorder ?? '1.5px solid #c7d2fe',
                boxShadow: dropShadow,
            }}>
                {initials || '?'}
            </div>
        );
    }

    return (
        <img
            src={`/public/usuario/${user.id}/image.png`}
            alt={title} title={title}
            {...dropProps}
            style={{ ...base, objectFit: 'cover', border: dropBorder ?? '1.5px solid #c7d2fe', boxShadow: dropShadow }}
            onError={() => setImgError(true)}
        />
    );
}

// ── Actividad node ────────────────────────────────────────────────────────

function NodeWithAddChild({ data, selected }: { data: any; selected: boolean }) {
    const tipo = data.tipo;
    const label = data.label ?? '';
    const crearNodos = data.crearNodos ?? false;
    const isActividad = tipo === 'actividad';
    const permitirSeleccionarUsuario = data.permitirSeleccionarUsuario ?? false;

    const topHandle = tipo !== 'inicio'
        ? createHandle('target', Position.Top)
        : null;

    const addButton = crearNodos && data.onAddChild
        ? (
            <div className="add-button col-md-12" style={{ marginTop: 4 }}>
                <button
                    style={{ width: '100%', cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); data.onAddChild(); }}
                >
                    Actividad
                </button>
            </div>
        )
        : <div className="add-button col-md-12" />;

    return (
        <div
            className={tipo === 'inicio'
                ? (selected ? 'nodo nodo-inicio selected' : 'nodo nodo-inicio')
                : (selected ? 'nodo selected' : 'nodo')}
            style={{
                boxShadow: selected ? '0 0 0 2px rgba(37,99,235,0.2)' : 'none',
                transition: 'all 0.15s ease',
                color: (data.habilitado || !isActividad) ? 'black' : 'lightgray',
                textAlign: 'center',
            }}
        >
            {topHandle}

            <div className="nodo-descripcion" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '4px 6px' }}>
                {/* Label flanked by user avatars */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%' }}>
                    {isActividad && permitirSeleccionarUsuario && <NodeUserAvatar user={data.usuario} size={28} onAssign={data.onAssignUsuario} />}
                    <h4 style={{ margin: 0, flex: 1, fontSize: 12, lineHeight: 1.2 }} className='nodo-label'>{label}
                        {<RequiereAutorizacionShield show={!!data.requiereAutorizacion} />}
                    </h4>
                    {isActividad && permitirSeleccionarUsuario && !!data.requiereAutorizacion && <NodeUserAvatar user={data.autoriza} size={28} onAssign={data.onAssignAutoriza} />}
                </div>

                {/* Add-child button at the bottom */}
                {addButton}
            </div>

            {createHandle('source', Position.Bottom)}
        </div>
    );
}

const nodeTypes = { actividad: NodeWithAddChild, fin: EndNodeFn, inicio: NodeWithAddChild };

/* ---------- COMPONENTE PRINCIPAL ---------- */

export default function WorkflowDesigner(props: any) {
    const { volver, editarTipoNodo, editarActividad, crearNodos, permitirEliminarNodo,
        workflow, empresaDiseno, onActivoChange, onRefresh, onReplace, permitirSeleccionarUsuario } = props;
    const saveEntity = props.saveEntity;

    const [nodos, setNodos, onNodesChange] = useNodesState(props.entity.nodos);
    const [edges, setEdges, onEdgesChange] = useEdgesState(props.entity.edges);

    const nodesRef = useRef<any[]>(props.entity.nodos);
    const edgesRef = useRef<any[]>(props.entity.edges);

    useEffect(() => {
        nodesRef.current = nodos;
    }, [nodos]);

    useEffect(() => {
        edgesRef.current = edges;
    }, [edges]);

    const [selectedNode, setSelectedNode] = React.useState(null);
    const [selectedEdge, setSelectedEdge] = React.useState(null);
    const [initialTabItem, setInitialTabItem] = React.useState('workflow');


    const selectNode = (nodeId: any) => {
        const currentNode = nodesRef.current.find(n => n.id === nodeId);
        if (!currentNode) return;
        setSelectedNode(currentNode);
        setSelectedEdge(null);
    }

    const selectEdge = (edgeId: any) => {
        const currentEdge = edgesRef.current.find(e => e.id === edgeId);
        if (!currentEdge) return;
        setSelectedEdge(currentEdge);
        setSelectedNode(null);
    }

    const onNodeClick = (_: any, node: any) => {
        selectNode(node.id);
    };
    const onEdgeClick = (_: any, edge: any) => {
        selectEdge(edge.id);
    }
    const onNodeDragStart = (
        event: React.MouseEvent,
        node: any
    ) => {
        selectNode(node.id);
    };

    /* ---------- ASIGNAR USUARIO A NODO (drag & drop) ---------- */

    // Stable ref so addNodeFromSelected ([] deps) can call it without stale closure
    const assignUserToNodeRef = useRef<(nodeId: string, field: 'usuario' | 'autoriza', user: any) => void>(() => { });

    const assignUserToNode = useCallback((nodeId: string, field: 'usuario' | 'autoriza', user: any) => {
        setNodos(nds => nds.map(n =>
            n.id !== nodeId ? n : { ...n, data: { ...n.data, [field]: user } }
        ));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    assignUserToNodeRef.current = assignUserToNode;

    /* ---------- AGREGAR HIJO ---------- */
    const updateNode = useCallback((currentNode: any) => {
        setNodos((nds) =>
            nds.map((n) =>
                n.id === currentNode.id
                    ? { ...currentNode, position: n.position }
                    : n
            )
        );

    }, []);

    const removeNode = useCallback((currentNode: any) => {

        setNodos((nds) => nds.filter((n) => n.id !== currentNode.id)); // eliminar nodo
        setEdges((eds) => eds.filter((e) => e.source !== currentNode.id && e.target !== currentNode.id));
    }, []);

    const removeEdge = useCallback((currentEdge: any) => {
        setEdges((nds) => nds.filter((n) => n.id !== currentEdge.id)); // eliminar edge
    }, []);



    const addNodeFromSelected = useCallback((parentId: string) => {
        const nodesCurrent = nodesRef.current;
        const edgesCurrent = edgesRef.current;

        const parent = nodesCurrent.find(n => n.id === parentId);
        if (!parent) return;

        const childrenEdges = edgesCurrent.filter(e => e.source === parentId);
        const childrenNodes = childrenEdges
            .map(e => nodesCurrent.find(n => n.id === e.target))
            .filter(Boolean) as any[];

        let position;
        if (childrenNodes.length > 0) {
            const lastChild = childrenNodes.reduce((a, b) =>
                a.position.y > b.position.y ? a : b
            );
            position = {
                x: lastChild.position.x + 150,
                y: lastChild.position.y,
            };
        } else {
            position = {
                x: parent.position.x,
                y: parent.position.y + 100,
            };
        }

        const newNodeId = crypto.randomUUID();

        const newNode = {
            id: newNodeId,
            type: 'actividad',
            tipo: 'actividad',
            position,
            data: {
                tipo: 'actividad',
                label: '-- Sin Categoría --',
                habilitado: true,
                onAddChild: () => addNodeFromSelected(newNodeId),
                onAssignUsuario: (u: any) => assignUserToNodeRef.current(newNodeId, 'usuario', u),
                onAssignAutoriza: (u: any) => assignUserToNodeRef.current(newNodeId, 'autoriza', u),
                crearNodos,
                permitirSeleccionarUsuario
            },
        };

        const newEdge = {
            id: crypto.randomUUID(),
            source: parentId,
            target: newNodeId,
        };

        setNodos(nds => [...nds, newNode]);
        setEdges(eds => [...eds, newEdge]);
    }, []);

    /* ---------- INICIALIZAR onAddChild ---------- */

    useEffect(() => {
        setNodos(
            props.entity.nodos.map((n: any) => ({
                ...n,
                type: n.tipo,
                data: {
                    tipo: n.tipo,
                    ...n.data,
                    habilitado: n.habilitado,
                    onAddChild: () => addNodeFromSelected(n.id),
                    onAssignUsuario: (u: any) => assignUserToNodeRef.current(n.id, 'usuario', u),
                    onAssignAutoriza: (u: any) => assignUserToNodeRef.current(n.id, 'autoriza', u),
                    crearNodos,
                    permitirSeleccionarUsuario,
                    requiereAutorizacion: n.requiereAutorizacion,
                },
            }))
        );
    }, [addNodeFromSelected]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ---------- CONEXIONES MANUALES ---------- */

    const onConnect = useCallback(
        (params: Edge | Connection) =>
            setEdges(eds => addEdge(params, eds)),
        []
    );

    /* ---------- GUARDAR ---------- */

    const saveWorkflow = () => {
        saveEntity({ nodos, edges });
        setInitialTabItem('workflow');
    };

    const cancelSelection = () => {
        setSelectedNode(null);
        setSelectedEdge(null);
    };


    const nodosAdaptados = nodos.map((n: any) => ({
        ...n,
        type: n.tipo,
    }));

    return (
        <div className='workflow-designer' style={{ display: 'flex', height: '100vh' }}>

            {/* ── Left: toolbar + canvas ─────────────────────────────── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <ReactFlowProvider>
                    {/* Toolbar lives inside ReactFlowProvider so it can use useReactFlow() */}
                    <WorkflowToolbar
                        saveWorkflow={saveWorkflow}
                        volver={volver}
                        onRefresh={onRefresh}
                        onReplace={onReplace}
                        selectedNode={selectedNode}
                        selectedEdge={selectedEdge}
                        removeNode={removeNode}
                        removeEdge={removeEdge}
                        onCancelSelection={cancelSelection}
                        addNodeFromSelected={addNodeFromSelected}
                        allowDelete={permitirEliminarNodo}
                        allowAddNode={crearNodos}
                    />
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <ReactFlow
                            nodes={nodosAdaptados}
                            edges={edges}
                            nodeTypes={nodeTypes}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            fitView
                            zoomOnScroll={true}
                            zoomOnPinch={true}
                            panOnScroll={false}
                            onNodeClick={onNodeClick}
                            onEdgeClick={onEdgeClick}
                            onPaneClick={cancelSelection}
                            selectNodesOnDrag={true}
                            onNodeDragStart={onNodeDragStart}
                        >
                            <Background />
                        </ReactFlow>
                    </div>
                </ReactFlowProvider>
            </div>

            {/* ── Right: properties panel ────────────────────────────── */}
            <div style={{ width: 300, borderLeft: '1px solid #e2e8f0', padding: 12, display: 'flex', flexDirection: 'column' }}>
                <WorkflowDisenoTabPage
                    initialTabItem={initialTabItem}
                    selectedNode={selectedNode}
                    selectedEdge={selectedEdge}
                    updateNode={updateNode}
                    removeNode={removeNode}
                    removeEdge={removeEdge}
                    addNodeFromSelected={addNodeFromSelected}
                    permitirEliminarNodo={permitirEliminarNodo}
                    editarTipoNodo={editarTipoNodo}
                    editarActividad={editarActividad}
                    permitirAgregarHijo={crearNodos}
                    saveWorkflow={saveWorkflow}
                    volver={volver}
                    workflow={workflow}
                    empresaDiseno={empresaDiseno}
                    onActivoChange={onActivoChange}
                    permitirSeleccionarUsuario={permitirSeleccionarUsuario}
                />
            </div>
        </div>
    );
}
